import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Activity, TrendingUp, TrendingDown, Minus, ShieldAlert, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { fetchRiskTone } from "@/lib/api";

const TONE = {
  "risk-on": { label: "RISK-ON", cls: "text-pos border-emerald-500/50", Icon: TrendingUp },
  neutral: { label: "NEUTRAL", cls: "text-zinc-300 border-zinc-600", Icon: Minus },
  cautious: { label: "CAUTIOUS", cls: "text-amber-term border-amber-500/50", Icon: Activity },
  "risk-off": { label: "RISK-OFF", cls: "text-neg border-red-500/50", Icon: TrendingDown },
};
const FCLS = { pos: "text-pos", flat: "text-zinc-400", warn: "text-amber-term", neg: "text-neg" };
const flvl = (s) => (s > 0 ? "pos" : s === 0 ? "flat" : s === -1 ? "warn" : "neg");

const Divider = () => <span className="h-5 w-px bg-zinc-800/70 hidden lg:inline-block" />;

// Macro Tone bar: fused risk verdict (VIX + 2s10s + PPI) + Risk-Off control.
// Risk-Off auto-arms when the tone flips to risk-off.
export default function MacroCommandBar({ vix, threshold, riskOffEnabled, onToggle, onThreshold, alignedCount }) {
  const toneQ = useQuery({ queryKey: ["risk-tone"], queryFn: fetchRiskTone, staleTime: 1000 * 60 * 15, refetchOnWindowFocus: false, retry: 1 });
  const tone = toneQ.data;
  const meta = TONE[tone?.tone] || TONE.neutral;

  const prev = useRef(null);
  useEffect(() => {
    const t = tone?.tone;
    if (t && prev.current && prev.current !== "risk-off" && t === "risk-off" && !riskOffEnabled) {
      onToggle(true);
      toast.warning("Risk-Off auto-armed", { description: "Macro tone flipped risk-off — VIX positioning monitor enabled." });
    }
    if (t) prev.current = t;
  }, [tone?.tone, riskOffEnabled, onToggle]);

  const topAccent =
    meta === TONE["risk-off"] ? "#ef4444"
    : meta === TONE.cautious ? "#f59e0b"
    : meta === TONE["risk-on"] ? "#10b981"
    : "#3f3f46";

  const breached = riskOffEnabled && vix != null && vix >= threshold;
  const active = riskOffEnabled && breached;
  const ShieldIcon = active ? ShieldAlert : ShieldCheck;
  const shieldCls = active ? "text-neg" : riskOffEnabled ? "text-pos" : "text-zinc-500";

  return (
    <div
      className="border border-zinc-800 bg-[#09090B] px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-2 transition-[border-color] duration-500"
      style={{ borderTop: `2px solid ${topAccent}` }}
      data-testid="macro-command-bar"
    >
      {/* Hero verdict */}
      <div className="flex items-center gap-2">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500">Macro Tone</span>
        {toneQ.isLoading ? (
          <span className="text-[11px] uppercase tracking-widest text-zinc-600 animate-pulse">reading…</span>
        ) : (
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 border text-[13px] font-semibold uppercase tracking-widest ${meta.cls}`} data-testid="macro-tone-verdict">
            <meta.Icon size={13} strokeWidth={2} /> {meta.label}
          </span>
        )}
        {tone?.factors?.length > 0 && (
          <span className="text-[11px] uppercase tracking-widest text-zinc-600 tabular-nums hidden sm:inline">score {tone.score > 0 ? "+" : ""}{tone.score}</span>
        )}
      </div>

      <Divider />

      {/* Fused factors (VIX shown once) */}
      {tone?.factors?.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] uppercase tracking-widest" data-testid="macro-factors">
          {tone.factors.map((f) => (
            <span key={f.key} className="inline-flex items-center gap-1.5" data-testid={`macro-factor-${f.key}`}>
              <span className="text-zinc-600">{f.label}</span>
              <span className={`font-semibold ${FCLS[flvl(f.score)]}`}>{f.state}</span>
              {f.value != null && f.key !== "pipeline" && (
                <span className="text-zinc-500 tabular-nums normal-case">{f.key === "curve" ? `${f.value > 0 ? "+" : ""}${f.value}pp` : f.value}</span>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Risk-Off control */}
      <div className={`flex items-center gap-2 ml-auto transition-opacity duration-300 ${riskOffEnabled ? "opacity-100" : "opacity-60"}`} data-testid="risk-off-control">
        <ShieldIcon size={16} strokeWidth={1.5} className={shieldCls} />
        <span className="text-[11px] uppercase tracking-widest text-zinc-500">Risk-Off</span>
        <span className={`text-[11px] uppercase tracking-widest font-semibold ${active ? "text-neg" : riskOffEnabled ? "text-pos" : "text-zinc-500"}`} data-testid="risk-off-state">
          {!riskOffEnabled ? "OFF" : active ? "ACTIVE" : "ARMED"}
        </span>
        {riskOffEnabled && (
          <>
            <input
              type="range" min="10" max="40" step="1" value={threshold}
              onChange={(e) => onThreshold(parseInt(e.target.value, 10))}
              data-testid="threshold-slider" className="w-24" style={{ accentColor: "hsl(var(--primary))" }}
              title={`VIX threshold ${threshold}`}
            />
            <span className="text-[11px] text-amber-term tabular-nums w-5 text-right">{threshold}</span>
            {active && (
              <span className="text-[11px] uppercase tracking-widest text-zinc-500" data-testid="aligned-count">
                <span className="text-zinc-100">{alignedCount}</span> aligned
              </span>
            )}
          </>
        )}
        <Switch
          checked={riskOffEnabled}
          onCheckedChange={onToggle}
          data-testid="risk-off-toggle"
          className="data-[state=checked]:bg-amber-500 data-[state=unchecked]:bg-zinc-700"
        />
      </div>
    </div>
  );
}
